import HighlightOption from './HighlightOption';
import NoteOption from './NoteOption';
import QuoteOption from './QuoteOption';
import TaskOption from './TaskOption';
import getDirectionalHint from '../utils/getDirectionalHint';
import { observer } from 'mobx-react-lite';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { IPoint } from 'office-ui-fabric-react/lib/Utilities';
import { logUsage } from 'owa-analytics';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ClientItem } from 'owa-mail-store';
import { isTimePanelWithToDoFeaturesAvailable } from 'owa-time-panel-bootstrap';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
    EXISTING_HIGHLIGHT_DATAPOINT,
    NEW_HIGHLIGHT_DATAPOINT,
    POPUP_GAPSPACE,
} from '../utils/constants';

const styles = require('./UserMarkupPopup.scss');

export interface UserMarkupPopupProps {
    target: IPoint | HTMLElement;
    selectedText: string;
    element: HTMLElement;
    item: ClientItem;
    isForExistingUserHighlight: boolean;
    isForExistingUserNote: boolean;
    selectionRange?: Range;
    selectionInstance?: number;
}

const UserMarkupPopup = observer(function UserMarkupPopup(props: UserMarkupPopupProps) {
    const {
        target,
        selectedText,
        element,
        item,
        isForExistingUserHighlight,
        isForExistingUserNote,
        selectionRange,
        selectionInstance,
    } = props;
    if (!item || !element || !selectedText) {
        return null;
    }

    return (
        <Callout
            isBeakVisible={false}
            gapSpace={POPUP_GAPSPACE}
            onDismiss={destroyPopupDiv}
            directionalHint={getDirectionalHint(selectedText)}
            target={isForExistingUserHighlight ? target : (target as IPoint)}>
            <div className={styles.userMarkupCalloutWrapper}>
                {isFeatureEnabled('rp-userMarkup') && !isForExistingUserNote && (
                    <HighlightOption
                        selectedText={selectedText}
                        element={element}
                        item={item}
                        isForExistingUserHighlight={isForExistingUserHighlight}
                        destroyPopupDivCallback={destroyPopupDiv}
                    />
                )}
                {isFeatureEnabled('rp-userMarkup') && !isForExistingUserHighlight && (
                    <NoteOption
                        target={target}
                        selectedText={selectedText}
                        element={element}
                        item={item}
                        isForExistingUserNote={isForExistingUserNote}
                        selectionRange={selectionRange}
                        selectionInstance={selectionInstance}
                        destroyPopupDivCallback={destroyPopupDiv}
                    />
                )}
                <QuoteOption
                    selectedText={selectedText}
                    item={item}
                    isForExistingUserHighlight={isForExistingUserHighlight}
                    destroyPopupDivCallback={destroyPopupDiv}
                />
                {isTimePanelWithToDoFeaturesAvailable() && (
                    <TaskOption
                        selectedText={selectedText}
                        item={item}
                        destroyPopupDivCallback={destroyPopupDiv}
                    />
                )}
            </div>
        </Callout>
    );
});

let popupDiv: HTMLDivElement = null;

function showUserMarkupPopup(
    target: IPoint | HTMLElement,
    selectedText: string,
    element: HTMLElement,
    item: ClientItem,
    isForExistingUserHighlight: boolean,
    isForExistingUserNote: boolean,
    selectionRange?: Range,
    selectionInstance?: number
) {
    if (!popupDiv) {
        createPopupDiv();
    }

    ReactDOM.render(
        <React.StrictMode>
            <UserMarkupPopup
                target={target}
                selectedText={selectedText}
                element={element}
                item={item}
                isForExistingUserHighlight={isForExistingUserHighlight}
                isForExistingUserNote={isForExistingUserNote}
                selectionRange={selectionRange}
                selectionInstance={selectionInstance}
            />
        </React.StrictMode>,
        popupDiv
    );

    // Log PopupShown datapoint
    logUsage('UserMarkup_PopupShown', [
        isForExistingUserHighlight ? EXISTING_HIGHLIGHT_DATAPOINT : NEW_HIGHLIGHT_DATAPOINT,
    ]);
}

function createPopupDiv() {
    popupDiv = document.createElement('div');
    document.body.appendChild(popupDiv);
}

export function destroyPopupDiv() {
    if (popupDiv) {
        ReactDOM.unmountComponentAtNode(popupDiv);
        popupDiv.parentNode.removeChild(popupDiv);
        popupDiv = null;
    }
}

export default showUserMarkupPopup;
